// src/components/TaskFilterBar.js
//
// Search-led filter bar (design handoff option 5b, the recommended direction).
//
// Replaces the 190px panel of six Material selects that never changed. Applied
// filters live as tokens *inside* the search field, the rarely-touched ones sit
// behind a Filters button carrying a count, and saved views sit underneath. The
// list now starts above the fold.
//
// There is deliberately **no status filter** in the bar — the client asked for
// filtering on template, operator, team and customer only. The saved-views row
// does carry two named states ("Needs attention", "Pending upload"), which is a
// different move: a view answers "what needs me now?", it is not status offered
// as a dimension to combine. When one is active it shows as a token in the
// search field so it can be seen and cleared like anything else.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button, IconButton, Popover, TextField, Checkbox,
  Select, MenuItem, FormControl, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  TuneOutlined as TuneIcon,
} from '@mui/icons-material';
import { collection, getCountFromServer, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useTemplates } from '../contexts/TemplateContext';
import useCachedData from '../hooks/useCachedData';
import { useWorkspaceReadable } from '../hooks/useClaimsReadySnapshot';
import { TRAK, LABEL_SX, railColor } from '../theme/trakTokens';
import {
  DATE_RANGE_PRESETS, DEFAULT_PRESET, describeWindow, isCustomPreset, localDayString, parseLocalDay,
} from '../utils/dateRangePresets';
import { NO_TEAM } from '../utils/teamFilter';
import { sortByName } from '../utils/sortByName';

const SAVED_VIEWS_KEY = (subscriberId) => `traklog.taskViews.${subscriberId || 'anon'}`;

const CONTROL_SX = {
  height: 34,
  borderRadius: '7px',
  font: `500 12.5px/1 ${TRAK.sans}`,
  color: TRAK.text,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: TRAK.borderSoft },
};

const PILL_SX = (active) => ({
  height: 25,
  px: 1.25,
  borderRadius: '13px',
  font: `${active ? 600 : 500} 11px/1 ${TRAK.sans}`,
  color: active ? '#fff' : 'rgba(13,51,32,.62)',
  bgcolor: active ? TRAK.ink : TRAK.fill,
  minWidth: 0,
  whiteSpace: 'nowrap',
  '&:hover': { bgcolor: active ? TRAK.greenDark : '#e9ece9' },
});

function todayRange() {
  const today = new Date();
  return { dateFrom: today, dateTo: today };
}

export const EMPTY_TASK_FILTERS = {
  // A named window, resolved by the API in the workspace's timezone. The
  // browser deliberately does not compute it — see utils/dateRangePresets.
  dateRange: DEFAULT_PRESET,
  dateField: 'submitted_at',
  templateId: ['all'],
  operatorId: 'all',
  customerId: 'all',
  teamId: 'all',
  hasPhotosOnly: false,
  status: null,
  idSearch: '',
};

// Built-in views. These are *named states*, not a status filter the user
// assembles — the brief removed the status control from the bar, and these
// answer a different question ("what needs me now?") rather than offering
// status as a dimension. `status` maps straight onto the Firestore query.
const COUNTS_REFRESH_MS = 5 * 60 * 1000;

export const BUILT_IN_VIEWS = [
  // Today's tasks in whichever mode is showing. In Live, `liveToday` asks
  // TaskList for the live window of today; without the view Live shows the
  // latest tasks with no date limit. In Dates, the "today" preset is resolved
  // in the workspace's timezone. It must not switch to Dates itself: that
  // dropped the live updates Live exists for.
  { id: 'today', name: 'Today', filters: { dateRange: 'today', liveToday: true } },
  // Tasks waiting on a person. A template with `approval_required` moves its
  // tasks to `in_review` on submission, and until this view existed nothing
  // told anybody: no email, no push, no queue — the task sat there until
  // somebody happened to open it. Migrated tasks do not appear, because a
  // legacy `vetted` maps to `submitted` rather than `in_review`.
  { id: 'approval', name: 'Needs approval', filters: { status: ['in_review'] }, countStatus: 'in_review', tone: 'warning' },
  { id: 'attention', name: 'Needs attention', filters: { status: ['rejected'] }, countStatus: 'rejected', tone: 'danger' },
  { id: 'pending', name: 'Pending upload', filters: { status: ['uploading'] }, countStatus: 'uploading' },
];

// A view's count carries its urgency: rejected work is a failure, work waiting
// on approval is a queue. Anything else is just a number.
const VIEW_TONE_COLOR = {
  danger: TRAK.dangerText,
  warning: TRAK.warnText,
};

/** Token shown inside the search field for one applied filter. */
function FilterToken({ kind, label, onClear }) {
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '6px', height: 26, px: 1.1,
        borderRadius: '6px', bgcolor: TRAK.successFill,
        font: `500 11.5px/1 ${TRAK.sans}`, color: TRAK.greenDark, whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" sx={{ opacity: 0.6 }}>{kind}</Box>
      <Box component="b" sx={{ fontWeight: 600 }}>{label}</Box>
      <IconButton
        size="small"
        aria-label={`Clear ${kind} filter`}
        onClick={onClear}
        sx={{ p: 0, color: 'inherit', opacity: 0.55, '&:hover': { opacity: 1 } }}
      >
        <CloseIcon sx={{ fontSize: 13 }} />
      </IconButton>
    </Box>
  );
}

/**
 * Searchable multi-select over templates. A workspace can have dozens, so the
 * popover shows a filter row and a checkbox list with the template's colour
 * dot — the same colour that becomes the card's rail.
 */
function TemplateMultiSelect({ templates, selectedIds, onChange, getColor }) {
  const [search, setSearch] = useState('');
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return templates;
    return templates.filter(t => (t.name || '').toLowerCase().includes(needle));
  }, [templates, search]);

  const toggle = (id) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter(x => x !== id)
      : [...selectedIds, id];
    onChange(next);
  };

  return (
    <Box sx={{ border: `1px solid ${selectedIds.length ? TRAK.green : TRAK.borderSoft}`, borderRadius: '7px', mb: 0.75 }}>
      <Box sx={{ p: 0.75 }}>
        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Filter ${templates.length} templates…`}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ fontSize: 15, color: TRAK.faint, mr: 0.75 }} />,
              sx: { height: 28, borderRadius: '6px', bgcolor: TRAK.fill, font: `400 12px/1 ${TRAK.sans}` },
            },
          }}
          sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
        />
        <Box sx={{ maxHeight: 190, overflowY: 'auto', mt: 0.5 }}>
          {visible.length === 0 && (
            <Typography sx={{ p: 1, font: `400 12px/1.4 ${TRAK.sans}`, color: TRAK.faint }}>
              No templates match.
            </Typography>
          )}
          {visible.map(template => {
            const checked = selectedIds.includes(template.id);
            return (
              <Box
                key={template.id}
                onClick={() => toggle(template.id)}
                role="checkbox"
                aria-checked={checked}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(template.id); } }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.1, px: 1, py: 0.85,
                  borderRadius: '6px', cursor: 'pointer',
                  bgcolor: checked ? '#f4fbf6' : 'transparent',
                  '&:hover': { bgcolor: '#f4fbf6' },
                }}
              >
                <Checkbox
                  checked={checked}
                  size="small"
                  tabIndex={-1}
                  onChange={() => {}}
                  sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 17 } }}
                  inputProps={{ 'aria-label': template.name }}
                />
                <Box
                  component="span"
                  sx={{
                    width: 7, height: 7, borderRadius: '2px', flex: 'none',
                    bgcolor: getColor(template.id) || TRAK.railNone,
                  }}
                />
                <Typography
                  component="span"
                  sx={{ flex: 1, minWidth: 0, font: `500 12.5px/1.3 ${TRAK.sans}`, color: TRAK.text }}
                >
                  {template.name}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function TaskFilterBar({
  currentFilters,
  onFilterChange,
  mode,
  onModeChange,
  subscriberId,
  matchingCount = null,
  totalCount = null,
  // Resolved by the API, not derived here — see utils/dateRangePresets.
  dateWindow = null,
  // Changes when the list approves, rejects, deletes or restores a task.
  countsRefreshKey = 0,
}) {
  const {
    getTemplatesForDropdown, getTemplateNameById, getTemplateColorById, refreshTemplatesIfEmpty,
  } = useTemplates();
  const [filtersAnchor, setFiltersAnchor] = useState(null);
  const [draftSearch, setDraftSearch] = useState('');
  // Date inputs are edited locally and only committed on blur / Enter — see the
  // date range controls below for why.
  const [draftDateFrom, setDraftDateFrom] = useState('');
  const [draftDateTo, setDraftDateTo] = useState('');
  const [savedViews, setSavedViews] = useState([]);
  // The saved view whose delete has been asked for but not yet confirmed.
  const [viewPendingDelete, setViewPendingDelete] = useState(null);
  // No view is selected until one is chosen: the unfiltered list is a state
  // of its own, not a view.
  const [activeViewId, setActiveViewId] = useState(null);
  const [viewCounts, setViewCounts] = useState({});
  const searchRef = useRef(null);

  const filters = useMemo(
    () => ({ ...EMPTY_TASK_FILTERS, ...todayRange(), ...(currentFilters || {}) }),
    [currentFilters]
  );
  const selectedTemplateIds = useMemo(
    () => (Array.isArray(filters.templateId) ? filters.templateId : [filters.templateId]).filter(id => id && id !== 'all'),
    [filters.templateId]
  );

  const templates = useMemo(
    () => getTemplatesForDropdown().filter(t => t.id !== 'all'),
    [getTemplatesForDropdown]
  );

  // The workspace to read for, or null until the ID token carries it: a read
  // in that gap is refused (see useClaimsReadySnapshot).
  const { ready: claimsReady, claimKey } = useWorkspaceReadable(subscriberId || null);
  const readableSubscriberId = claimsReady ? subscriberId : null;

  const fetchOperators = useCallback(async () => {
    const subscriberId = readableSubscriberId;
    if (!subscriberId) return [];
    return new Promise((resolve, reject) => {
      const ref = collection(db, 'subscribers', subscriberId, 'operators');
      const unsubscribe = onSnapshot(
        query(ref, where('deleted', '!=', true), orderBy('deleted'), orderBy('created_at', 'desc')),
        (snap) => {
          resolve(sortByName(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(op => !op.deleted)));
          unsubscribe();
        },
        (error) => { reject(error); unsubscribe(); }
      );
    });
  }, [readableSubscriberId]);

  const fetchTeams = useCallback(async () => {
    const subscriberId = readableSubscriberId;
    if (!subscriberId) return [];
    return new Promise((resolve, reject) => {
      const ref = collection(db, 'subscribers', subscriberId, 'teams');
      const unsubscribe = onSnapshot(
        query(ref, orderBy('team_name')),
        (snap) => { resolve(sortByName(snap.docs.map(d => ({ id: d.id, ...d.data() })))); unsubscribe(); },
        (error) => { reject(error); unsubscribe(); }
      );
    });
  }, [readableSubscriberId]);

  const fetchCustomers = useCallback(async () => {
    const subscriberId = readableSubscriberId;
    if (!subscriberId) return [];
    return new Promise((resolve, reject) => {
      const ref = collection(db, 'subscribers', subscriberId, 'customers');
      const unsubscribe = onSnapshot(
        query(ref, orderBy('name')),
        (snap) => {
          resolve(sortByName(
            snap.docs.map(d => ({ id: d.id, ...d.data() }))
              .filter(c => !c.deleted && c.active !== false)
              .map(c => ({ id: c.id, name: c.name }))
          ));
          unsubscribe();
        },
        (error) => { reject(error); unsubscribe(); }
      );
    });
  }, [readableSubscriberId]);

  const { data: operators, refresh: refreshOperators } = useCachedData('operators', fetchOperators, { subscriberId: readableSubscriberId, ttl: 10 * 60 * 1000 });
  const { data: teams, refresh: refreshTeams } = useCachedData('teams', fetchTeams, { subscriberId: readableSubscriberId, ttl: 15 * 60 * 1000 });
  const { data: customers } = useCachedData('customers', fetchCustomers, { subscriberId: readableSubscriberId });

  // Opening Filters with an empty list is the moment a failed read shows. Read
  // it again behind the popover (no spinner, nothing cleared): an empty
  // template, operator or team list is almost always a read that was refused
  // or dropped, not a workspace that has none. Customers are left out: plenty
  // of workspaces really have none, and that would be a read on every open.
  const openFilters = (anchor) => {
    setFiltersAnchor(anchor);
    if (refreshTemplatesIfEmpty) refreshTemplatesIfEmpty();
    [[operators, refreshOperators], [teams, refreshTeams]]
      .forEach(([list, refresh]) => {
        if (readableSubscriberId && typeof refresh === 'function' && (!Array.isArray(list) || list.length === 0)) {
          Promise.resolve(refresh()).catch(() => {});
        }
      });
  };

  useEffect(() => { setDraftSearch(filters.idSearch || ''); }, [filters.idSearch]);

  // Counts are aggregations, not listeners, so they go stale. They used to be
  // read once when the page opened: approving the last task in "Needs
  // approval" left the chip saying 3. They are re-read when the list changes
  // a task (countsRefreshKey), every five minutes while the tab is visible, and on
  // returning to the tab, which covers changes made elsewhere: a phone
  // uploading, another reviewer.
  const [countsTick, setCountsTick] = useState(0);
  // Another workspace's counts must not survive the merge below.
  useEffect(() => { setViewCounts({}); }, [subscriberId]);
  useEffect(() => {
    const bump = () => {
      if (document.visibilityState === 'visible') setCountsTick(t => t + 1);
    };
    // Every five minutes, not every minute: a count bills a read per
    // thousand tasks it counts, per open tab, and returning to the tab or
    // changing a task refreshes it straight away anyway (#868).
    const id = setInterval(bump, COUNTS_REFRESH_MS);
    document.addEventListener('visibilitychange', bump);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', bump);
    };
  }, []);

  // A count on "Needs attention" is the point of the view — a rejected task
  // nobody reassigns is invisible otherwise. `getCountFromServer` is a single
  // aggregation read rather than fetching the documents.
  useEffect(() => {
    if (!subscriberId || !claimsReady) return undefined;
    let cancelled = false;
    (async () => {
      const counts = {};
      await Promise.all(BUILT_IN_VIEWS.filter(v => v.countStatus).map(async (view) => {
        try {
          const snap = await getCountFromServer(query(
            collection(db, 'tasks'),
            where('subscriber_id', '==', subscriberId),
            where('deleted', '==', false),
            where('status', '==', view.countStatus),
          ));
          counts[view.id] = snap.data().count;
        } catch (error) {
          // A missing index or a rules denial must not take the bar down; the
          // view still works, it just shows no count.
          console.warn(`View count unavailable for ${view.id}:`, error?.message);
        }
      }));
      // Merged, so a view whose re-read failed keeps its last count rather
      // than losing it.
      if (!cancelled) setViewCounts(prev => ({ ...prev, ...counts }));
    })();
    return () => { cancelled = true; };
  }, [subscriberId, countsRefreshKey, countsTick, claimsReady, claimKey]);

  // Saved views are per user + workspace, so a supervisor's "Woodlands today"
  // doesn't follow them into another subscriber's data.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SAVED_VIEWS_KEY(subscriberId));
      setSavedViews(raw ? JSON.parse(raw) : []);
    } catch {
      setSavedViews([]);
    }
  }, [subscriberId]);

  const persistViews = (views) => {
    setSavedViews(views);
    try {
      localStorage.setItem(SAVED_VIEWS_KEY(subscriberId), JSON.stringify(views));
    } catch {
      // A full or blocked localStorage costs the user persistence, not the filter.
    }
  };

  const operatorName = (operatorId) => {
    const op = (operators || []).find(o => o.id === operatorId);
    return op?.operatorName || op?.name || operatorId;
  };
  const customerName = (customerId) => (customers || []).find(c => c.id === customerId)?.name || customerId;
  const teamName = (teamId) => {
    if (teamId === NO_TEAM) return 'No team';
    const team = (teams || []).find(t => t.id === teamId);
    return team?.team_name || team?.name || teamId;
  };

  const apply = (patch) => {
    const next = { ...filters, ...patch };
    setActiveViewId(null);
    onFilterChange(next);
  };

  // Team filters the loaded page by membership: tasks carry an operator, not a
  // team, so the ids are resolved here and matched in TaskList.
  // A team is now a filter the server applies, against `team_ids` denormalised
  // onto the task. It used to resolve the team to its member operator ids and
  // hand them to TaskList, which matched them against the page of 20 rows it
  // had already loaded — so picking a team removed non-members from page one
  // rather than filtering the workspace, and looked like it worked.
  //
  // Expanding to operator ids server-side was not available either: Firestore
  // caps `IN` at 30 values and multiplies disjunctions, so a site team of 50
  // could not be expressed at all. `array-contains` on one team id can.
  const applyTeam = (teamId) => {
    apply({ teamId: !teamId ? 'all' : teamId });
  };

  const clearAll = () => {
    setActiveViewId(null);
    onFilterChange({ ...EMPTY_TASK_FILTERS, ...todayRange() });
  };

  const activeFilterCount =
    selectedTemplateIds.length +
    (filters.operatorId && filters.operatorId !== 'all' ? 1 : 0) +
    (filters.customerId && filters.customerId !== 'all' ? 1 : 0) +
    (filters.teamId && filters.teamId !== 'all' ? 1 : 0) +
    (filters.hasPhotosOnly ? 1 : 0) +
    (Array.isArray(filters.status) && filters.status.length > 0 ? 1 : 0);

  // Clicking the selected view again switches it off, back to no view.
  const applyView = (view) => {
    if (activeViewId === view.id) {
      setActiveViewId(null);
      onFilterChange({ ...EMPTY_TASK_FILTERS, ...todayRange() });
      return;
    }
    setActiveViewId(view.id);
    onFilterChange({ ...EMPTY_TASK_FILTERS, ...todayRange(), ...view.filters });
  };

  const saveCurrentView = () => {
    // eslint-disable-next-line no-alert
    const name = window.prompt('Name this view', 'My view');
    if (!name) return;
    const view = {
      id: `view-${Date.now()}`,
      name,
      filters: {
        templateId: filters.templateId,
        operatorId: filters.operatorId,
        customerId: filters.customerId,
        teamId: filters.teamId,
        hasPhotosOnly: filters.hasPhotosOnly,
        idSearch: filters.idSearch,
      },
    };
    persistViews([...savedViews, view]);
    setActiveViewId(view.id);
  };

  const formatDateForInput = (date) => localDayString(date);

  // Keep the drafts in step with filters changed elsewhere (saved views, Clear
  // filters, the resume flow) without re-rendering the field mid-edit.
  useEffect(() => {
    setDraftDateFrom(formatDateForInput(filters.dateFrom));
    setDraftDateTo(formatDateForInput(filters.dateTo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.dateFrom, filters.dateTo]);

  const commitDate = (key, value) => {
    const next = value ? parseLocalDay(value) : null;
    if (value && !next) return;                          // half-typed, ignore
    const current = filters[key];
    const same = (!next && !current)
      || (next && current instanceof Date && next.getTime() === current.getTime());
    if (same) return;                                    // no refetch for a no-op
    apply({ [key]: next });
  };

  return (
    <Box
      data-tour="task-filters"
      sx={{
        bgcolor: TRAK.surface,
        border: `1px solid ${TRAK.hairline}`,
        borderRadius: '10px',
        p: '14px 16px',
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
        {/* Search field carrying the applied filters as tokens */}
        <Box
          onClick={() => searchRef.current?.focus()}
          sx={{
            flex: '1 1 360px', minWidth: 260, display: 'flex', alignItems: 'center', gap: '7px',
            flexWrap: 'wrap', minHeight: 38, px: 1.5, py: '5px',
            border: `1px solid ${TRAK.border}`, borderRadius: '9px', cursor: 'text',
            '&:focus-within': { borderColor: TRAK.green },
          }}
        >
          <SearchIcon sx={{ fontSize: 16, color: TRAK.faint }} />

          {selectedTemplateIds.map(id => (
            <FilterToken
              key={id}
              kind="template"
              label={getTemplateNameById(id) || 'Template'}
              onClear={() => apply({ templateId: selectedTemplateIds.filter(x => x !== id).length
                ? selectedTemplateIds.filter(x => x !== id)
                : ['all'] })}
            />
          ))}
          {filters.operatorId !== 'all' && (
            <FilterToken kind="operator" label={operatorName(filters.operatorId)} onClear={() => apply({ operatorId: 'all' })} />
          )}
          {filters.teamId !== 'all' && (
            <FilterToken kind="team" label={teamName(filters.teamId)} onClear={() => applyTeam('all')} />
          )}
          {filters.customerId !== 'all' && (
            <FilterToken kind="customer" label={customerName(filters.customerId)} onClear={() => apply({ customerId: 'all' })} />
          )}
          {filters.hasPhotosOnly && (
            <FilterToken kind="has" label="photos only" onClear={() => apply({ hasPhotosOnly: false })} />
          )}
          {Array.isArray(filters.status) && filters.status.length > 0 && (
            <FilterToken
              kind="view"
              label={BUILT_IN_VIEWS.find(v => v.filters.status?.[0] === filters.status[0])?.name || filters.status.join(', ')}
              onClear={() => { setActiveViewId(null); onFilterChange({ ...filters, status: null }); }}
            />
          )}

          <Box
            component="input"
            ref={searchRef}
            value={draftSearch}
            onChange={(e) => setDraftSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') apply({ idSearch: draftSearch.trim() });
              if (e.key === 'Escape') { setDraftSearch(''); apply({ idSearch: '' }); }
            }}
            placeholder="Search task ID…"
            aria-label="Search tasks by ID"
            sx={{
              flex: '1 1 120px', minWidth: 90, border: 'none', outline: 'none', bgcolor: 'transparent',
              font: `400 12.5px/1 ${TRAK.sans}`, color: TRAK.text,
              '&::placeholder': { color: 'rgba(13,51,32,.38)' },
            }}
          />
          <Typography
            component="span"
            sx={{
              font: `500 10px/1 ${TRAK.mono}`, px: '5px', py: '3px', borderRadius: '4px',
              bgcolor: 'rgba(13,51,32,.07)', color: 'rgba(13,51,32,.45)',
            }}
          >
            Enter
          </Typography>
        </Box>

        {/* Live | Dates */}
        <Box sx={{ display: 'flex', p: '2px', borderRadius: '8px', bgcolor: 'rgba(13,51,32,.07)' }}>
          {[
            { value: 'live', label: 'Live' },
            { value: 'dateRange', label: 'Dates' },
          ].map(option => {
            const active = mode === option.value;
            return (
              <Button
                key={option.value}
                onClick={() => onModeChange(option.value)}
                sx={{
                  minWidth: 0, px: 1.4, py: 0.9, borderRadius: '6px',
                  font: `${active ? 600 : 500} 12px/1 ${TRAK.sans}`,
                  color: active ? TRAK.ink : TRAK.muted,
                  bgcolor: active ? TRAK.surface : 'transparent',
                  boxShadow: active ? '0 1px 2px rgba(13,51,32,.12)' : 'none',
                  '&:hover': { bgcolor: active ? TRAK.surface : 'rgba(255,255,255,.5)' },
                }}
              >
                {active && option.value === 'live' && (
                  <Box
                    component="span"
                    sx={{
                      width: 6, height: 6, mr: 0.75, borderRadius: '50%', bgcolor: TRAK.accent,
                      boxShadow: '0 0 0 3px rgba(46,204,122,.25)',
                    }}
                  />
                )}
                {option.label}
              </Button>
            );
          })}
        </Box>

        {mode === 'dateRange' && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              select
              size="small"
              label="Range"
              value={filters.dateRange || DEFAULT_PRESET}
              onChange={(e) => apply({ dateRange: e.target.value })}
              slotProps={{ inputLabel: { shrink: true }, input: { sx: CONTROL_SX } }}
              sx={{ minWidth: 130 }}
            >
              {DATE_RANGE_PRESETS.map(p => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </TextField>

            {isCustomPreset(filters.dateRange) && (
              <>
                {/* Committed on blur / Enter, not on change. A native date input
                    fires change while you are still typing — half-entered years
                    like 0002 are valid dates — so every keystroke used to refetch
                    the whole list and the page appeared to reload under you. */}
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  value={draftDateFrom}
                  onChange={(e) => setDraftDateFrom(e.target.value)}
                  onBlur={() => commitDate('dateFrom', draftDateFrom)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitDate('dateFrom', draftDateFrom); }}
                  slotProps={{ inputLabel: { shrink: true }, input: { sx: CONTROL_SX } }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  value={draftDateTo}
                  onChange={(e) => setDraftDateTo(e.target.value)}
                  onBlur={() => commitDate('dateTo', draftDateTo)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitDate('dateTo', draftDateTo); }}
                  slotProps={{ inputLabel: { shrink: true }, input: { sx: CONTROL_SX } }}
                />
              </>
            )}

            {/* What the server actually resolved, in the workspace's timezone.
                Shown rather than derived here: an admin abroad would otherwise
                see their own Monday, which is the bug this replaced. */}
            {dateWindow && (
              <Typography
                sx={{ font: `500 11px/1 ${TRAK.sans}`, color: 'rgba(13,51,32,.55)', whiteSpace: 'nowrap' }}
                title={`Resolved in ${dateWindow.timezone} by ${dateWindow.date_field === 'created_at' ? 'capture' : 'submission'} time`}
              >
                {describeWindow(dateWindow)}
              </Typography>
            )}
          </Box>
        )}

        <Button
          variant="outlined"
          startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openFilters(e.currentTarget)}
          sx={{
            height: 38, px: 1.6, borderRadius: '8px',
            font: `600 12.5px/1 ${TRAK.sans}`,
            border: `1px solid ${activeFilterCount ? TRAK.green : TRAK.borderSoft}`,
            bgcolor: activeFilterCount ? TRAK.successFill : TRAK.surface,
            color: activeFilterCount ? TRAK.greenDark : TRAK.ink,
            '&:hover': { border: `1px solid ${TRAK.green}`, bgcolor: TRAK.successFill },
          }}
        >
          Filters
          {activeFilterCount > 0 && (
            <Box
              component="span"
              sx={{
                ml: 0.75, px: '5px', py: '2px', borderRadius: '8px',
                bgcolor: TRAK.green, color: '#fff', font: `600 10px/1 ${TRAK.mono}`,
              }}
            >
              {activeFilterCount}
            </Box>
          )}
        </Button>
      </Box>

      {/* Saved views + match count */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.4, flexWrap: 'wrap' }}>
        <Typography component="span" sx={{ ...LABEL_SX, color: 'rgba(13,51,32,.38)', mr: 0.25 }}>
          Views
        </Typography>

        {BUILT_IN_VIEWS.map(view => (
          <Button key={view.id} sx={PILL_SX(activeViewId === view.id)} aria-pressed={activeViewId === view.id} onClick={() => applyView(view)}>
            {view.name}
            {viewCounts[view.id] > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 0.75,
                  font: `600 10px/1 ${TRAK.mono}`,
                  color: activeViewId === view.id
                    ? '#fff'
                    : (VIEW_TONE_COLOR[view.tone] || TRAK.muted),
                }}
              >
                {viewCounts[view.id]}
              </Box>
            )}
          </Button>
        ))}

        {/* Deleting a saved view used to be a shift-click, which only the
            tooltip mentioned — so the pills read as save-only. Each one now
            carries the same inline dismiss control as a FilterToken, at the
            same resting opacity, and confirms first: a view holds a filter
            combination that has to be rebuilt by hand if it goes by accident,
            and it lives in localStorage where there is nothing to restore
            from. */}
        {savedViews.map(view => (
          <Box
            key={view.id}
            sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
          >
            <Button
              sx={{ ...PILL_SX(activeViewId === view.id), pr: 2.75 }}
              aria-pressed={activeViewId === view.id}
              onClick={() => applyView(view)}
            >
              {view.name}
            </Button>
            <Tooltip title={`Delete “${view.name}”`}>
              <IconButton
                size="small"
                aria-label={`Delete view ${view.name}`}
                onClick={() => setViewPendingDelete(view)}
                sx={{
                  position: 'absolute', right: 4, p: 0,
                  color: activeViewId === view.id ? '#fff' : 'rgba(13,51,32,.62)',
                  opacity: 0.55,
                  '&:hover': { opacity: 1, bgcolor: 'transparent' },
                }}
              >
                <CloseIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ))}

        <Button
          onClick={saveCurrentView}
          sx={{
            height: 25, px: 1.25, borderRadius: '13px', minWidth: 0,
            border: `1px dashed rgba(13,51,32,.2)`,
            font: `500 11px/1 ${TRAK.sans}`, color: 'rgba(13,51,32,.45)', whiteSpace: 'nowrap',
          }}
        >
          + Save current
        </Button>

        <Box sx={{ flex: 1 }} />

        {activeFilterCount > 0 || filters.idSearch ? (
          <Button onClick={clearAll} sx={{ font: `500 12px/1 ${TRAK.sans}`, color: TRAK.green, minWidth: 0 }}>
            Clear
          </Button>
        ) : null}

        {matchingCount !== null && (
          <Typography component="span" sx={{ font: `400 11.5px/1 ${TRAK.sans}`, color: 'rgba(13,51,32,.45)' }}>
            <Box component="b" sx={{ fontWeight: 600, color: TRAK.ink }}>{matchingCount}</Box>
            {totalCount !== null ? ` of ${totalCount} match` : ' shown'}
          </Typography>
        )}
      </Box>

      {/* Delete a saved view */}
      <Dialog
        open={Boolean(viewPendingDelete)}
        onClose={() => setViewPendingDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ font: `600 16px/1.3 ${TRAK.sans}` }}>Delete this view?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ font: `400 13.5px/1.5 ${TRAK.sans}` }}>
            “{viewPendingDelete?.name}” will be removed from the Views row. The
            filters it holds are not saved anywhere else, so rebuilding it means
            setting them again. Tasks are not affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewPendingDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              persistViews(savedViews.filter(v => v.id !== viewPendingDelete.id));
              // Deleting the view that is currently applied leaves its filters
              // in place; only the pill goes. Dropping the selection stops the
              // bar highlighting a view that no longer exists.
              if (activeViewId === viewPendingDelete.id) setActiveViewId(null);
              setViewPendingDelete(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filters popover */}
      <Popover
        open={Boolean(filtersAnchor)}
        anchorEl={filtersAnchor}
        onClose={() => setFiltersAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: 300, mt: 0.75, p: 1.75, borderRadius: '10px',
              border: `1px solid rgba(13,51,32,.12)`, boxShadow: TRAK.shadowPopover,
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography sx={{ font: `600 12px/1 ${TRAK.sans}`, color: TRAK.ink }}>Filters</Typography>
          <Box sx={{ flex: 1 }} />
          <Button onClick={clearAll} sx={{ minWidth: 0, font: `500 11.5px/1 ${TRAK.sans}`, color: TRAK.green }}>
            Reset
          </Button>
        </Box>

        <Typography component="div" sx={{ ...LABEL_SX, mb: 0.9 }}>Template</Typography>
        <TemplateMultiSelect
          templates={templates}
          selectedIds={selectedTemplateIds}
          getColor={(id) => railColor(getTemplateColorById(id))}
          onChange={(ids) => apply({ templateId: ids.length ? ids : ['all'] })}
        />
        <Typography sx={{ font: `400 10.5px/1 ${TRAK.sans}`, color: TRAK.faint, mb: 1.75 }}>
          {selectedTemplateIds.length} of {templates.length} selected · type to filter
        </Typography>

        <Typography component="div" sx={{ ...LABEL_SX, mb: 0.9 }}>Team</Typography>
        <FormControl fullWidth size="small" sx={{ mb: 1.25 }}>
          <Select
            value={filters.teamId || 'all'}
            onChange={(e) => applyTeam(e.target.value)}
            sx={CONTROL_SX}
            inputProps={{ 'aria-label': 'Team' }}
          >
            <MenuItem value="all">All teams</MenuItem>
            {/* A real state, not a gap: a workspace can use teams and still
                have operators on none of them — 124 of Katoen Natie's 548 are.
                Distinct from a task whose teams were never established, which
                this does not match. */}
            <MenuItem value={NO_TEAM}>No team</MenuItem>
            {(teams || []).map(team => (
              <MenuItem key={team.id} value={team.id}>{team.team_name || team.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography component="div" sx={{ ...LABEL_SX, mb: 0.9 }}>Operator</Typography>
        <FormControl fullWidth size="small" sx={{ mb: 1.25 }}>
          <Select
            value={filters.operatorId || 'all'}
            onChange={(e) => apply({ operatorId: e.target.value })}
            sx={CONTROL_SX}
            inputProps={{ 'aria-label': 'Operator' }}
          >
            <MenuItem value="all">All operators</MenuItem>
            {(operators || [])
              .filter(op => filters.teamId === 'all' || (op.teams || op.teamIds || []).includes(filters.teamId))
              .map(op => (
                <MenuItem key={op.id} value={op.id}>{op.operatorName || op.name || op.id}</MenuItem>
              ))}
          </Select>
        </FormControl>

        <Typography component="div" sx={{ ...LABEL_SX, mb: 0.9 }}>Customer</Typography>
        <FormControl fullWidth size="small" sx={{ mb: 1.25 }}>
          <Select
            value={filters.customerId || 'all'}
            onChange={(e) => apply({ customerId: e.target.value })}
            sx={CONTROL_SX}
            inputProps={{ 'aria-label': 'Customer' }}
          >
            <MenuItem value="all">All customers</MenuItem>
            {(customers || []).map(customer => (
              <MenuItem key={customer.id} value={customer.id}>{customer.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          onClick={() => apply({ hasPhotosOnly: !filters.hasPhotosOnly })}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.1, pt: 1.25,
            borderTop: `1px solid rgba(13,51,32,.08)`, cursor: 'pointer',
          }}
        >
          <Checkbox
            checked={Boolean(filters.hasPhotosOnly)}
            onChange={() => {}}
            size="small"
            sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 18 } }}
            inputProps={{ 'aria-label': 'Has photos only' }}
          />
          <Typography sx={{ font: `500 12px/1 ${TRAK.sans}`, color: TRAK.text }}>Has photos only</Typography>
        </Box>
      </Popover>
    </Box>
  );
}

export default TaskFilterBar;
